import { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { useWatch } from "react-hook-form";

import { Box } from "@mui/material";

import {
  measureTypeName,
  idParametersOptionsName,
  idParameterName,
  idObjectsOptionsName,
  idObjectName,
} from "../../constants";
import {
  IObjectOption,
  IParameterOption,
  IChartData,
  IChartDataSignal,
  IChartDataOrbit,
  IFaultFrequencyChartCursor,
  ANALYTICS_VIEW,
  ANALYTICS_MEASURE_TYPE,
  TRAIN_ROTATION_DIRECTION,
} from "../../interfaces";
import { MultilineChart } from "../multiline-chart";
import { ITrendChartTimeBounds } from "../multiline-chart/time-domain";
import { MultilineChartSignal } from "../multiline-chart-signal";
import { MultilineChartOrbit } from "../multiline-chart-orbit";
import { ITableData } from "../table/interfaces";
import { AnalyticsTable } from "../table";
import {
  TChartExportHandler,
  TChartExportCanvasFactory,
  exportChartGroup,
  getChartExportFileName,
  TChartExportFormat,
} from "../chart-export";
import { hasTableRowsData } from "../../utils/dataAvailability";
import { splitAmplitudePhase } from "../../utils/amplitude-phase";
import { getLineColor } from "../../utils/line-colors";
import { getSeriesKey } from "../../utils/series-key";

export interface IAnalyticsView {
  chartData?: IChartData[] | null;
  tableData?: ITableData | null;
  chartDataSignal?: IChartDataSignal[] | null;
  chartDataOrbit?: IChartDataOrbit[] | null;
  trendTimeBounds?: ITrendChartTimeBounds | null;
  trainRotationDirection?: TRAIN_ROTATION_DIRECTION | null;
  view: ANALYTICS_VIEW;
  isPolarTrendChecked: boolean;
  isCombinedAmpPhaseChecked: boolean;
  isHarmonicCursorsChecked?: boolean;
  isSubHarmonicCursorsChecked?: boolean;
  faultFrequencies?: IFaultFrequencyChartCursor[] | null;
  spectrumDisplayCascade3D?: boolean;
  signalDisplayCascade3D?: boolean;
  registerResetHandler?: (handler: (() => void) | null) => void;
  registerExportHandler?: (handler: TChartExportHandler | null) => void;
  onZoomStateChange?: (isZoomed: boolean) => void;
}

export const AnalyticsView: FC<IAnalyticsView> = props => {
  const {
    chartData,
    tableData,
    chartDataSignal,
    chartDataOrbit,
    trendTimeBounds = null,
    trainRotationDirection = null,
    view,
    isPolarTrendChecked,
    isCombinedAmpPhaseChecked = false,
    isHarmonicCursorsChecked = false,
    isSubHarmonicCursorsChecked = false,
    faultFrequencies = null,
    spectrumDisplayCascade3D = true,
    signalDisplayCascade3D = true,
    registerResetHandler,
    registerExportHandler,
    onZoomStateChange,
  } = props;

  const measureType = useWatch({ name: measureTypeName });
  const idObjectsOptions = useWatch({ name: idObjectsOptionsName }) as IObjectOption[];
  const idObject = useWatch({ name: idObjectName });
  const idParametersOptions = useWatch({ name: idParametersOptionsName }) as IParameterOption[];
  const idParameter = useWatch({ name: idParameterName });
  const isSpectrumMeasure = measureType === ANALYTICS_MEASURE_TYPE.SPECTRUM;
  const isSignalFormMeasure = measureType === ANALYTICS_MEASURE_TYPE.SIGNAL_FORM;
  const isSignalChartMeasure = isSpectrumMeasure || isSignalFormMeasure;

  const resetHandlersRef = useRef<Record<string, () => void>>({});
  const exportHandlersRef = useRef<Record<string, TChartExportHandler>>({});
  const zoomStatesRef = useRef<Record<string, boolean>>({});
  const datasetVisibilityRef = useRef<Record<string, boolean>>({});
  const amplitudeExportCanvasRef = useRef<TChartExportCanvasFactory | null>(null);
  const phaseExportCanvasRef = useRef<TChartExportCanvasFactory | null>(null);
  const sharedLegendContainerId = "legend-container-amplitude-phase-shared";

  const parameterTitle = Array.isArray(idParameter)
    ? idParameter
        .map(id => idParametersOptions?.find(option => option.id === id)?.title)
        .filter(Boolean)
        .join(", ")
    : idParametersOptions?.find(option => option.id === idParameter)?.title;
  const objectTitle = idObjectsOptions?.find(option => option.id === idObject)?.title;
  const chartTitle = parameterTitle && objectTitle ? `${objectTitle}: ${parameterTitle}` : "";

  const syncResetHandler = useCallback(() => {
    const handlers = Object.values(resetHandlersRef.current);

    registerResetHandler?.(
      handlers.length
        ? () => {
            Object.values(resetHandlersRef.current).forEach(handler => handler());
          }
        : null,
    );
  }, [registerResetHandler]);

  const exportHandler = useCallback((format: TChartExportFormat) => {
    const fileName = getChartExportFileName(format);
    const exportFormat = format === "png" ? "image/png" : "image/jpeg";

    if (Object.values(exportHandlersRef.current).length) {
      Object.values(exportHandlersRef.current).forEach(handler => handler(format));
      return;
    }

    if (amplitudeExportCanvasRef.current && phaseExportCanvasRef.current) {
      exportChartGroup([amplitudeExportCanvasRef.current(), phaseExportCanvasRef.current()], exportFormat, fileName);
    }
  }, []);

  const syncExportHandler = useCallback(() => {
    registerExportHandler?.(exportHandler);
  }, [exportHandler, registerExportHandler]);

  const syncZoomState = useCallback(() => {
    onZoomStateChange?.(Object.values(zoomStatesRef.current).some(Boolean));
  }, [onZoomStateChange]);

  const createRegisterReset = useCallback(
    (key: string) => (handler: (() => void) | null) => {
      if (handler) {
        resetHandlersRef.current[key] = handler;
      } else {
        delete resetHandlersRef.current[key];
      }

      syncResetHandler();
    },
    [syncResetHandler],
  );

  const createRegisterExport = useCallback(
    (key: string) => (handler: TChartExportHandler | null) => {
      if (handler) {
        exportHandlersRef.current[key] = handler;
      } else {
        delete exportHandlersRef.current[key];
      }

      syncExportHandler();
    },
    [syncExportHandler],
  );

  const createZoomStateHandler = useCallback(
    (key: string) => (isZoomed: boolean) => {
      if (isZoomed) {
        zoomStatesRef.current[key] = true;
      } else {
        delete zoomStatesRef.current[key];
      }

      syncZoomState();
    },
    [syncZoomState],
  );

  const registerSignalReset = useMemo(() => createRegisterReset("signal"), [createRegisterReset]);
  const registerOrbitReset = useMemo(() => createRegisterReset("orbit"), [createRegisterReset]);
  const registerCombinedReset = useMemo(() => createRegisterReset("combined"), [createRegisterReset]);
  const registerAmplitudeReset = useMemo(() => createRegisterReset("amplitude"), [createRegisterReset]);
  const registerPhaseReset = useMemo(() => createRegisterReset("phase"), [createRegisterReset]);

  const registerSignalExport = useMemo(() => createRegisterExport("signal"), [createRegisterExport]);
  const registerOrbitExport = useMemo(() => createRegisterExport("orbit"), [createRegisterExport]);
  const registerCombinedExport = useMemo(() => createRegisterExport("combined"), [createRegisterExport]);
  const handleSignalZoomState = useMemo(() => createZoomStateHandler("signal"), [createZoomStateHandler]);
  const handleOrbitZoomState = useMemo(() => createZoomStateHandler("orbit"), [createZoomStateHandler]);
  const handleCombinedZoomState = useMemo(() => createZoomStateHandler("combined"), [createZoomStateHandler]);
  const handleAmplitudeZoomState = useMemo(() => createZoomStateHandler("amplitude"), [createZoomStateHandler]);
  const handlePhaseZoomState = useMemo(() => createZoomStateHandler("phase"), [createZoomStateHandler]);

  const amplitudeChartRef = useRef<any>(null);
  const phaseChartRef = useRef<any>(null);
  const { amplitudeCharts, phaseCharts } = useMemo(() => splitAmplitudePhase(chartData ?? []), [chartData]);

  const colorBySeries = useMemo(() => {
    const map = new Map<string, string>();
    let colorIndex = 0;

    (chartData ?? []).forEach(series => {
      if (series.idPoint == null || !series.parameterName) {
        return;
      }

      const key = getSeriesKey(series.idPoint, series.parameterName)!;

      if (!map.has(key)) {
        map.set(key, getLineColor(colorIndex++));
      }
    });

    return map;
  }, [chartData]);

  useEffect(() => {
    syncExportHandler();
    return () => {
      registerExportHandler?.(null);
    };
  }, [syncExportHandler]);

  useEffect(() => {
    return () => {
      resetHandlersRef.current = {};
      exportHandlersRef.current = {};
      zoomStatesRef.current = {};
      registerResetHandler?.(null);
      registerExportHandler?.(null);
      onZoomStateChange?.(false);
    };
  }, [onZoomStateChange, registerExportHandler, registerResetHandler]);

  useEffect(() => {
    if (!chartData) return;
    chartData.forEach(series => {
      const key = String(series.idPoint);
      if (!(key in datasetVisibilityRef.current)) {
        datasetVisibilityRef.current[key] = true;
      }
    });
  }, [chartData]);

  if (view === ANALYTICS_VIEW.CHART) {
    if (isSignalChartMeasure) {
      return chartDataSignal ? (
        <MultilineChartSignal
          data={chartDataSignal}
          chartTitle={chartTitle}
          enableKeyboardCursorNavigation={isSpectrumMeasure}
          showHarmonicCursors={isSpectrumMeasure && isHarmonicCursorsChecked}
          showSubHarmonicCursors={isSpectrumMeasure && isSubHarmonicCursorsChecked}
          displayCascade3D={isSpectrumMeasure ? spectrumDisplayCascade3D : signalDisplayCascade3D}
          faultFrequencies={isSpectrumMeasure ? faultFrequencies : null}
          registerReset={registerSignalReset}
          registerExport={registerSignalExport}
          onZoomStateChange={handleSignalZoomState}
        />
      ) : null;
    }

    if (
      measureType === ANALYTICS_MEASURE_TYPE.ORBIT ||
      (measureType === ANALYTICS_MEASURE_TYPE.TREND && isPolarTrendChecked)
    ) {
      return chartDataOrbit ? (
        <MultilineChartOrbit
          data={chartDataOrbit}
          chartTitle={chartTitle}
          isPolarTrendChecked={measureType === ANALYTICS_MEASURE_TYPE.ORBIT || isPolarTrendChecked}
          polarTrendMode={measureType === ANALYTICS_MEASURE_TYPE.TREND && isPolarTrendChecked}
          trainRotationDirection={trainRotationDirection}
          registerReset={registerOrbitReset}
          registerExport={registerOrbitExport}
          onZoomStateChange={handleOrbitZoomState}
        />
      ) : null;
    }

    const isAmplitudePhase = chartData && chartData.every(item => item?.isAmplitudePhase === true);
    if (chartData && isAmplitudePhase) {
      if (isCombinedAmpPhaseChecked) {
        return (
          <MultilineChart
            data={chartData}
            colorBySeries={colorBySeries}
            allAmplitudePhaseData={chartData}
            chartTitle={chartTitle}
            legendContainerId="legend-container-line-combined"
            timeBounds={trendTimeBounds}
            registerReset={registerCombinedReset}
            registerExport={registerCombinedExport}
            onZoomStateChange={handleCombinedZoomState}
            datasetVisibility={datasetVisibilityRef.current}
            onDatasetVisibilityChange={(seriesKey, visible) => {
              datasetVisibilityRef.current[seriesKey] = visible;
            }}
          />
        );
      }

      return (
        <Box display="flex" flexDirection="column" height="100%" minHeight={0} overflow="hidden">
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <MultilineChart
              data={amplitudeCharts}
              colorBySeries={colorBySeries}
              allAmplitudePhaseData={chartData}
              chartTitle={`${chartTitle} (Амплитуда)`}
              legendContainerId={sharedLegendContainerId}
              hideLegend={true}
              timeBounds={trendTimeBounds}
              externalChartRef={amplitudeChartRef}
              syncChartRef={phaseChartRef}
              registerReset={registerAmplitudeReset}
              onZoomStateChange={handleAmplitudeZoomState}
              datasetVisibility={datasetVisibilityRef.current}
              onDatasetVisibilityChange={(seriesKey, visible) => {
                datasetVisibilityRef.current[seriesKey] = visible;
              }}
              exportCanvasFactoryRef={amplitudeExportCanvasRef}
            />
          </Box>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <MultilineChart
              data={phaseCharts}
              colorBySeries={colorBySeries}
              allAmplitudePhaseData={chartData}
              chartTitle={`${chartTitle} (Фаза)`}
              legendContainerId={sharedLegendContainerId}
              timeBounds={trendTimeBounds}
              externalChartRef={phaseChartRef}
              syncChartRef={amplitudeChartRef}
              registerReset={registerPhaseReset}
              onZoomStateChange={handlePhaseZoomState}
              datasetVisibility={datasetVisibilityRef.current}
              onDatasetVisibilityChange={(seriesKey, visible) => {
                datasetVisibilityRef.current[seriesKey] = visible;
              }}
              exportCanvasFactoryRef={phaseExportCanvasRef}
              suppressInternalLegendContainer={true}
            />
          </Box>
          <Box id={sharedLegendContainerId} />
        </Box>
      );
    }

    return chartData ? (
      <MultilineChart
        data={chartData}
        colorBySeries={colorBySeries}
        chartTitle={chartTitle}
        legendContainerId="legend-container-line"
        timeBounds={trendTimeBounds}
        registerReset={registerCombinedReset}
        registerExport={registerCombinedExport}
        onZoomStateChange={handleCombinedZoomState}
      />
    ) : null;
  }

  if (view === ANALYTICS_VIEW.TABLE && hasTableRowsData(tableData)) {
    return <AnalyticsTable data={tableData} />;
  }

  return null;
};
